/**
 * ProductPageNavigation - Smooth scrolling navigation system for product detail pages
 * Implements sticky navigation with active section highlighting and smooth scrolling behavior
 */
class ProductPageNavigation {
  constructor() {
    this.sections = [];
    this.navLinks = [];
    this.activeSection = null;
    this.scrollOffset = 160; // Offset for sticky header (80px) + navigation (80px)
    this.isScrolling = false;
    this.scrollTimeout = null;
    this.intersectionObserver = null;
    this.resizeObserver = null;
    this.performanceMetrics = {
      initTime: performance.now(),
      scrollEvents: 0,
      intersectionEvents: 0
    };
    
    // Respect user's motion preferences
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    // Performance monitoring
    this.measurePerformance = this.throttle(this.logPerformanceMetrics.bind(this), 5000);
    
    this.init();
  }
  
  /**
   * Initialize navigation and scroll behavior
   */
  init() {
    // Critical path - immediate execution
    // Skip navigation removed - not needed
    this.updateAuthenticationBasedVisibility();
    this.createStickyNavigation();
    this.setupSections();
    this.optimizeIntersectionObserver();
    this.setupClickHandlers();
    this.setupKeyboardNavigation();
    this.updateActiveSection();
    
    // Handle window resize with throttling
    window.addEventListener('resize', this.throttle(() => {
      this.performanceMetrics.scrollEvents++;
      this.updateActiveSection();
    }, 250), { passive: true });
    
    // Lazy load non-critical features
    this.lazyLoadFeatures();
    
    // Mark initialization complete
    this.performanceMetrics.initComplete = performance.now();
    console.log(`ProductPageNavigation initialized in ${(this.performanceMetrics.initComplete - this.performanceMetrics.initTime).toFixed(2)}ms`);
  }
  
  /**
   * Create sticky navigation bar with section links
   */
  createStickyNavigation() {
    console.log('Creating sticky navigation...');
    let productContentWrapper = document.querySelector('.product-content-wrapper');
    
    // Fallback: try to find alternative containers
    if (!productContentWrapper) {
      productContentWrapper = document.querySelector('.main-product-content');
      if (!productContentWrapper) {
        productContentWrapper = document.querySelector('.product-content-sections');
        if (!productContentWrapper) {
          console.error('ProductPageNavigation: No suitable container found');
          console.log('Available elements:', document.querySelectorAll('[class*="product"]'));
          return;
        }
      }
    }
    
    // Check if navigation already exists
    if (document.querySelector('.product-sticky-navigation')) {
      console.log('ProductPageNavigation: Navigation already exists');
      return;
    }
    
    const navigation = document.createElement('div');
    navigation.className = 'product-sticky-navigation';
    navigation.setAttribute('role', 'navigation');
    navigation.setAttribute('aria-label', 'Product sections navigation');
    
    const navContainer = document.createElement('div');
    navContainer.className = 'nav-container';
    
    const navList = document.createElement('ul');
    navList.className = 'nav-list';
    navList.setAttribute('role', 'list');
    
    // Define navigation items based on sections present in the page
    const navItems = this.getNavigationItems();
    
    if (navItems.length === 0) {
      console.warn('ProductPageNavigation: No navigation items found');
      return;
    }
    
    console.log('ProductPageNavigation: Creating navigation with items:', navItems);
    
    navItems.forEach(item => {
      const listItem = document.createElement('li');
      listItem.className = 'nav-item';
      listItem.setAttribute('role', 'listitem');
      
      const link = document.createElement('a');
      link.href = `#${item.id}`;
      link.className = 'nav-link';
      link.textContent = item.label;
      link.setAttribute('data-section', item.id);
      link.setAttribute('role', 'button');
      link.setAttribute('aria-label', `Navigate to ${item.label} section`);
      
      listItem.appendChild(link);
      navList.appendChild(listItem);
      this.navLinks.push(link);
    });
    
    navContainer.appendChild(navList);
    navigation.appendChild(navContainer);
    
    // Insert navigation before the main content
    productContentWrapper.insertBefore(navigation, productContentWrapper.firstChild);
  }
  
  /**
   * Get navigation items based on sections present in the page
   * Only shows links for sections that actually exist
   */
  getNavigationItems() {
    // Define all possible sections in display order
    const allItems = [
      { id: 'overview', label: 'Overview' },
      { id: 'media', label: 'Media' },
      { id: 'features', label: 'Features' },
      { id: 'support', label: 'Support' },
      { id: 'provider', label: 'Developer' },
      { id: 'pricing', label: 'Pricing' }
    ];
    
    // Filter to only include sections that exist on the page
    return allItems.filter(item => document.getElementById(item.id));
  }
  
  /**
   * Setup sections array and add IDs if missing
   */
  setupSections() {
    const sectionElements = document.querySelectorAll('.product-section');
    this.sections = Array.from(sectionElements);
    
    // Ensure all sections have proper IDs and accessibility attributes
    this.sections.forEach((section, index) => {
      if (!section.id) {
        // Fallback ID assignment if sections don't have IDs
        const headings = section.querySelectorAll('h3, h2');
        if (headings.length > 0) {
          const heading = headings[0];
          const id = this.generateIdFromText(heading.textContent);
          section.id = id;
        } else {
          section.id = `section-${index}`;
        }
      }
      
      // Add accessibility attributes
      section.setAttribute('role', 'region');
      section.setAttribute('tabindex', '-1'); // Allow programmatic focus
      const heading = section.querySelector('h3, h2');
      if (heading) {
        section.setAttribute('aria-labelledby', heading.id || `heading-${section.id}`);
        if (!heading.id) {
          heading.id = `heading-${section.id}`;
        }
        // Ensure proper heading hierarchy
        if (heading.tagName === 'H3') {
          heading.setAttribute('role', 'heading');
          heading.setAttribute('aria-level', '2');
        }
      }
    });
  }
  
  /**
   * Setup Intersection Observer for efficient scroll tracking (legacy method)
   */
  setupIntersectionObserver() {
    // Redirect to optimized version
    this.optimizeIntersectionObserver();
  }
  
  /**
   * Setup click handlers for navigation links
   */
  setupClickHandlers() {
    this.navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const sectionId = link.getAttribute('data-section');
        this.scrollToSection(sectionId);
        
        // Update focus for accessibility
        setTimeout(() => {
          const section = document.getElementById(sectionId);
          if (section) {
            section.focus();
            section.scrollIntoView({ block: 'start' });
          }
        }, 500);
      });
    });
  }  
 
 /**
   * Setup keyboard navigation support
   */
  setupKeyboardNavigation() {
    document.addEventListener('keydown', (event) => {
      // Ctrl/Cmd + Arrow keys for section navigation
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.navigateToNextSection();
          this.announceNavigation('next');
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.navigateToPreviousSection();
          this.announceNavigation('previous');
        }
      }
      
      // Additional keyboard shortcuts (Alt + number keys)
      if (event.altKey && !event.ctrlKey && !event.metaKey) {
        switch(event.key) {
          case '1':
          case '2':
          case '3':
          case '4':
          case '5':
          case '6':
            event.preventDefault();
            const sectionIndex = parseInt(event.key) - 1;
            if (this.sections[sectionIndex]) {
              this.scrollToSection(this.sections[sectionIndex].id);
              this.announceNavigation('direct', this.sections[sectionIndex].id);
            }
            break;
        }
      }
    });
  }
  
  /**
   * Smooth scroll to section with proper offset
   */
  scrollToSection(sectionId) {
    const section = document.getElementById(sectionId);
    if (!section) return;
    
    this.isScrolling = true;
    
    const offsetTop = section.offsetTop - this.scrollOffset;
    const scrollBehavior = this.prefersReducedMotion.matches ? 'auto' : 'smooth';
    
    window.scrollTo({
      top: offsetTop,
      behavior: scrollBehavior
    });
    
    // Clear scrolling flag after animation completes
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.isScrolling = false;
      this.updateActiveSection();
    }, 1000);
    
    // Immediately update active nav item
    this.setActiveNavItem(sectionId);
  }
  
  /**
   * Navigate to next section
   */
  navigateToNextSection() {
    const currentIndex = this.getCurrentSectionIndex();
    const nextIndex = Math.min(currentIndex + 1, this.sections.length - 1);
    
    if (nextIndex !== currentIndex) {
      this.scrollToSection(this.sections[nextIndex].id);
    }
  }
  
  /**
   * Navigate to previous section
   */
  navigateToPreviousSection() {
    const currentIndex = this.getCurrentSectionIndex();
    const prevIndex = Math.max(currentIndex - 1, 0);
    
    if (prevIndex !== currentIndex) {
      this.scrollToSection(this.sections[prevIndex].id);
    }
  }
  
  /**
   * Get current section index based on scroll position
   */
  getCurrentSectionIndex() {
    const scrollPosition = window.scrollY + this.scrollOffset + 50;
    
    for (let i = this.sections.length - 1; i >= 0; i--) {
      const section = this.sections[i];
      if (scrollPosition >= section.offsetTop) {
        return i;
      }
    }
    return 0;
  }
  
  /**
   * Update active navigation based on scroll position
   */
  updateActiveSection() {
    if (this.isScrolling) return;
    
    const scrollPosition = window.scrollY + this.scrollOffset + 50;
    let activeId = null;
    
    for (let i = this.sections.length - 1; i >= 0; i--) {
      const section = this.sections[i];
      if (scrollPosition >= section.offsetTop) {
        activeId = section.id;
        break;
      }
    }
    
    if (activeId && activeId !== this.activeSection) {
      this.setActiveNavItem(activeId);
    }
  }  

  /**
   * Set active navigation item
   */
  setActiveNavItem(sectionId) {
    this.activeSection = sectionId;
    
    this.navLinks.forEach(link => {
      const isActive = link.getAttribute('data-section') === sectionId;
      link.classList.toggle('active', isActive);
      link.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
  
  /**
   * Generate ID from text content
   */
  generateIdFromText(text) {
    return text.toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }
  
  /**
   * Throttle function for performance optimization
   */
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
  
  /**
   * Debounce function for performance optimization
   */
  debounce(func, delay) {
    let timeoutId;
    return function() {
      const args = arguments;
      const context = this;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(context, args), delay);
    };
  }
  
  /**
   * Log performance metrics
   */
  logPerformanceMetrics() {
    const currentTime = performance.now();
    const totalTime = currentTime - this.performanceMetrics.initTime;
    
    console.log('ProductPageNavigation Performance Metrics:', {
      totalRuntime: `${totalTime.toFixed(2)}ms`,
      scrollEvents: this.performanceMetrics.scrollEvents,
      intersectionEvents: this.performanceMetrics.intersectionEvents,
      sectionsCount: this.sections.length,
      navLinksCount: this.navLinks.length,
      memoryUsage: performance.memory ? {
        used: `${(performance.memory.usedJSHeapSize / 1024 / 1024).toFixed(2)}MB`,
        total: `${(performance.memory.totalJSHeapSize / 1024 / 1024).toFixed(2)}MB`
      } : 'Not available'
    });
  }
  
  /**
   * Optimize intersection observer for better performance
   */
  optimizeIntersectionObserver() {
    // Use passive event listeners where possible
    const options = {
      root: null,
      rootMargin: `-${this.scrollOffset}px 0px -50% 0px`,
      threshold: [0, 0.25, 0.5, 0.75, 1]
    };
    
    // Disconnect existing observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    this.intersectionObserver = new IntersectionObserver((entries) => {
      this.performanceMetrics.intersectionEvents++;
      
      if (this.isScrolling) return;
      
      // Use requestAnimationFrame for smooth updates
      requestAnimationFrame(() => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.25) {
            this.setActiveNavItem(entry.target.id);
          }
        });
      });
    }, options);
    
    // Observe all visible sections
    this.sections.forEach(section => {
      if (section.offsetHeight > 0) { // Only observe visible sections
        this.intersectionObserver.observe(section);
      }
    });
  }
  
  /**
   * Setup resize observer for dynamic layout changes
   */
  setupResizeObserver() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.debounce(() => {
        this.updateActiveSection();
        this.optimizeIntersectionObserver();
      }, 150));
      
      // Observe the main content area
      const contentArea = document.querySelector('.product-content-sections');
      if (contentArea) {
        this.resizeObserver.observe(contentArea);
      }
    }
  }
  
  /**
   * Lazy load non-critical features
   */
  lazyLoadFeatures() {
    // Delay non-critical features until after initial render
    requestIdleCallback(() => {
      this.setupResizeObserver();
      this.measurePerformance();
    }, { timeout: 2000 });
  }
  
  /**
   * Announce navigation changes to screen readers
   */
  announceNavigation(type, sectionId = null) {
    const announcer = this.getOrCreateAnnouncer();
    let message = '';
    
    switch(type) {
      case 'next':
        const nextIndex = this.getCurrentSectionIndex() + 1;
        if (this.sections[nextIndex]) {
          const heading = this.sections[nextIndex].querySelector('h3, h2');
          const sectionName = heading ? heading.textContent : 'Next section';
          message = `Navigated to ${sectionName}`;
        }
        break;
      case 'previous':
        const prevIndex = this.getCurrentSectionIndex() - 1;
        if (this.sections[prevIndex]) {
          const heading = this.sections[prevIndex].querySelector('h3, h2');
          const sectionName = heading ? heading.textContent : 'Previous section';
          message = `Navigated to ${sectionName}`;
        }
        break;
      case 'direct':
        const section = document.getElementById(sectionId);
        if (section) {
          const heading = section.querySelector('h3, h2');
          const sectionName = heading ? heading.textContent : 'Section';
          message = `Navigated to ${sectionName}`;
        }
        break;
    }
    
    if (message) {
      announcer.textContent = message;
    }
  }
  
  /**
   * Get or create screen reader announcer element
   */
  getOrCreateAnnouncer() {
    let announcer = document.getElementById('navigation-announcer');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'navigation-announcer';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.style.position = 'absolute';
      announcer.style.left = '-10000px';
      announcer.style.width = '1px';
      announcer.style.height = '1px';
      announcer.style.overflow = 'hidden';
      document.body.appendChild(announcer);
    }
    return announcer;
  }
  
  /**
   * Add skip navigation functionality
   */
  addSkipNavigation() {
    const skipNav = document.createElement('a');
    skipNav.href = '#main-content';
    skipNav.textContent = 'Skip to main content';
    skipNav.className = 'skip-navigation';
    skipNav.setAttribute('tabindex', '1');
    
    // Insert at the beginning of the page
    document.body.insertBefore(skipNav, document.body.firstChild);
  }
  
  /**
   * Update navigation based on section visibility
   * Note: Authentication-based hiding removed - pricing now visible to all users
   */
  updateAuthenticationBasedVisibility() {
    // Update sections array to reflect current visibility
    this.sections = Array.from(document.querySelectorAll('.product-section'));
  }
  
  /**
   * Handle authentication state changes (for SPA scenarios)
   */
  onAuthenticationChange(isLoggedIn) {
    if (isLoggedIn) {
      document.body.classList.add('customer-logged-in');
    } else {
      document.body.classList.remove('customer-logged-in');
    }
    
    this.updateAuthenticationBasedVisibility();
    
    // Recreate navigation if needed
    const existingNav = document.querySelector('.product-sticky-navigation');
    if (existingNav) {
      existingNav.remove();
      this.navLinks = [];
      this.createStickyNavigation();
      this.setupClickHandlers();
    }
  }
  
  /**
   * Cleanup method for removing event listeners
   */
  destroy() {
    // Disconnect observers
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    // Clear timeouts
    clearTimeout(this.scrollTimeout);
    
    // Remove navigation element
    const navigation = document.querySelector('.product-sticky-navigation');
    if (navigation) {
      navigation.remove();
    }
    
    // Remove announcer
    const announcer = document.getElementById('navigation-announcer');
    if (announcer) {
      announcer.remove();
    }
    
    // Log final performance metrics
    this.logPerformanceMetrics();
    console.log('ProductPageNavigation destroyed');
  }
}

// Initialize ProductPageNavigation when DOM is ready
function initializeProductNavigation() {
  console.log('Attempting to initialize ProductPageNavigation...');
  
  // Check if we're on a product page
  if (!document.querySelector('.exchange-product-detail')) {
    console.log('ProductPageNavigation not initialized - no .exchange-product-detail element found');
    return false;
  }
  
  // Check if required elements exist
  const contentSections = document.querySelector('.product-content-sections');
  if (!contentSections) {
    console.log('ProductPageNavigation waiting - .product-content-sections not found yet');
    return false;
  }
  
  console.log('Initializing ProductPageNavigation...');
  window.productPageNavigation = new ProductPageNavigation();
  console.log('ProductPageNavigation initialized successfully');
  
  // Expose authentication change handler globally
  window.updateProductPageAuth = function(isLoggedIn) {
    if (window.productPageNavigation) {
      window.productPageNavigation.onAuthenticationChange(isLoggedIn);
    }
  };
  
  return true;
}

// Try to initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  if (!initializeProductNavigation()) {
    // If initialization failed, try again after a short delay
    setTimeout(() => {
      if (!initializeProductNavigation()) {
        console.warn('ProductPageNavigation initialization failed after retry');
      }
    }, 500);
  }
});

// Also try to initialize when the window loads (as a fallback)
window.addEventListener('load', function() {
  if (!window.productPageNavigation) {
    initializeProductNavigation();
  }
});

// Handle page visibility changes to optimize performance
document.addEventListener('visibilitychange', function() {
  if (window.productPageNavigation) {
    if (document.hidden) {
      // Pause intersection observer when page is hidden
      if (window.productPageNavigation.intersectionObserver) {
        window.productPageNavigation.intersectionObserver.disconnect();
      }
      if (window.productPageNavigation.resizeObserver) {
        window.productPageNavigation.resizeObserver.disconnect();
      }
    } else {
      // Resume observers when page becomes visible
      window.productPageNavigation.optimizeIntersectionObserver();
      window.productPageNavigation.setupResizeObserver();
    }
  }
});

/**
 * Media Carousel - Full-width preview with arrow navigation
 * Supports: Shopify product media + external URLs (images, YouTube, Vimeo)
 */
class MediaGallery {
  constructor() {
    this.gallery = document.querySelector('.media-gallery-carousel');
    if (!this.gallery) return;
    
    this.previewArea = this.gallery.querySelector('.media-preview-area');
    this.previewContent = this.gallery.querySelector('.media-preview-content');
    this.urlContent = this.gallery.querySelector('.media-url-content');
    this.prevBtn = this.gallery.querySelector('.media-nav-prev');
    this.nextBtn = this.gallery.querySelector('.media-nav-next');
    this.currentSpan = this.gallery.querySelector('.media-current');
    this.totalSpan = this.gallery.querySelector('.media-total');
    
    this.mediaItems = [];
    this.currentIndex = 0;
    
    this.init();
  }
  
  init() {
    this.buildMediaList();
    this.setupNavigation();
    this.showMedia(0);
    this.updateCounter();
    this.updateNavButtons();
  }
  
  buildMediaList() {
    // Get product media from data elements
    const dataItems = this.gallery.querySelectorAll('.media-item-data');
    dataItems.forEach(item => {
      this.mediaItems.push({
        type: item.dataset.type || 'image',
        src: item.dataset.src,
        alt: item.dataset.alt || ''
      });
    });
    
    // Get URL-based media from metafield
    try {
      const urlsData = this.gallery.dataset.mediaUrls;
      if (urlsData && urlsData !== '[]' && urlsData !== '{}') {
        let parsed = JSON.parse(urlsData);
        
        // Handle single object
        if (!Array.isArray(parsed) && parsed.url) {
          parsed = [parsed];
        }
        
        if (Array.isArray(parsed)) {
          parsed.forEach(item => {
            const type = this.detectMediaType(item.url, item.type);
            this.mediaItems.push({
              type: type,
              src: item.url,
              alt: item.title || ''
            });
          });
        }
      }
    } catch (e) {
      console.warn('Failed to parse media URLs:', e);
    }
    
    // Update total count
    if (this.totalSpan) {
      this.totalSpan.textContent = this.mediaItems.length || 1;
    }
  }
  
  detectMediaType(url, explicitType) {
    if (explicitType) return explicitType;
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('vimeo.com')) return 'vimeo';
    if (url.includes('storylane.io') || url.includes('loom.com')) return 'iframe';
    return 'image';
  }
  
  extractYouTubeId(url) {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  }
  
  setupNavigation() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.navigate(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.navigate(1));
    }
    
    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.isInViewport()) return;
      if (e.key === 'ArrowLeft') this.navigate(-1);
      if (e.key === 'ArrowRight') this.navigate(1);
    });
  }
  
  isInViewport() {
    const rect = this.gallery.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }
  
  navigate(direction) {
    const newIndex = this.currentIndex + direction;
    if (newIndex >= 0 && newIndex < this.mediaItems.length) {
      this.showMedia(newIndex);
    }
  }
  
  showMedia(index) {
    if (index < 0 || index >= this.mediaItems.length) return;
    
    this.currentIndex = index;
    const item = this.mediaItems[index];
    
    // Hide all existing preview items
    this.previewContent.querySelectorAll('.media-preview-item').forEach(el => {
      el.classList.remove('active');
      el.style.display = 'none';
    });
    
    // Clear URL content
    if (this.urlContent) this.urlContent.innerHTML = '';
    
    // Show the selected media
    if (item.type === 'youtube') {
      const videoId = this.extractYouTubeId(item.src);
      if (videoId && this.urlContent) {
        this.urlContent.innerHTML = `
          <iframe src="https://www.youtube.com/embed/${videoId}?rel=0" 
                  width="100%" 
                  height="450" 
                  frameborder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowfullscreen>
          </iframe>
        `;
      }
    } else if (item.type === 'vimeo') {
      const videoId = item.src.split('/').pop().split('?')[0];
      if (this.urlContent) {
        this.urlContent.innerHTML = `
          <iframe src="https://player.vimeo.com/video/${videoId}" 
                  width="100%" 
                  height="450" 
                  frameborder="0" 
                  allow="autoplay; fullscreen; picture-in-picture" 
                  allowfullscreen>
          </iframe>
        `;
      }
    } else if (item.type === 'iframe') {
      if (this.urlContent) {
        this.urlContent.innerHTML = `
          <iframe src="${item.src}" 
                  width="100%" 
                  height="450" 
                  frameborder="0" 
                  allow="autoplay; fullscreen; picture-in-picture" 
                  allowfullscreen>
          </iframe>
        `;
      }
    } else if (item.type === 'image' || !item.type) {
      if (this.urlContent) {
        this.urlContent.innerHTML = `<img src="${item.src}" alt="${item.alt}" style="max-width: 100%; max-height: 450px; object-fit: contain;">`;
      }
    }
    
    this.updateCounter();
    this.updateNavButtons();
  }
  
  updateCounter() {
    if (this.currentSpan) {
      this.currentSpan.textContent = this.currentIndex + 1;
    }
  }
  
  updateNavButtons() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= this.mediaItems.length - 1;
    }
    
    // Hide arrows if only one item
    if (this.mediaItems.length <= 1) {
      if (this.prevBtn) this.prevBtn.style.display = 'none';
      if (this.nextBtn) this.nextBtn.style.display = 'none';
      const counter = this.gallery.querySelector('.media-counter');
      if (counter) counter.style.display = 'none';
    }
  }
}

// Initialize Media Gallery when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  if (document.querySelector('.media-gallery-carousel')) {
    window.mediaGallery = new MediaGallery();
  }
});

/**
 * Fullscreen Media Modal - Handles fullscreen preview of media
 */
class FullscreenMediaModal {
  constructor(mediaGallery) {
    this.mediaGallery = mediaGallery;
    this.modal = document.querySelector('.media-fullscreen-modal');
    this.fullscreenBtn = document.querySelector('.media-fullscreen-btn');
    this.closeBtn = document.querySelector('.fullscreen-close-btn');
    this.prevBtn = document.querySelector('.fullscreen-nav-prev');
    this.nextBtn = document.querySelector('.fullscreen-nav-next');
    this.mediaWrapper = document.querySelector('.fullscreen-media-wrapper');
    this.currentSpan = document.querySelector('.fullscreen-current');
    this.totalSpan = document.querySelector('.fullscreen-total');
    
    this.isOpen = false;
    this.currentIndex = 0;
    
    if (this.modal && this.fullscreenBtn) {
      this.init();
    }
  }
  
  init() {
    // Open fullscreen
    this.fullscreenBtn.addEventListener('click', () => {
      this.open(this.mediaGallery.currentIndex);
    });
    
    // Close fullscreen
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }
    
    // Navigation
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.navigate(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.navigate(1));
    }
    
    // Close on overlay click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal || e.target.classList.contains('fullscreen-overlay')) {
        this.close();
      }
    });
    
    // Keyboard controls
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;
      
      if (e.key === 'Escape') {
        this.close();
      } else if (e.key === 'ArrowLeft') {
        this.navigate(-1);
      } else if (e.key === 'ArrowRight') {
        this.navigate(1);
      }
    });
  }
  
  open(index = 0) {
    this.isOpen = true;
    this.currentIndex = index;
    this.modal.style.display = 'block';
    document.body.style.overflow = 'hidden';
    
    // Update total count
    if (this.totalSpan) {
      this.totalSpan.textContent = this.mediaGallery.mediaItems.length;
    }
    
    this.showMedia(index);
  }
  
  close() {
    this.isOpen = false;
    this.modal.style.display = 'none';
    document.body.style.overflow = '';
  }
  
  navigate(direction) {
    const newIndex = this.currentIndex + direction;
    if (newIndex >= 0 && newIndex < this.mediaGallery.mediaItems.length) {
      this.showMedia(newIndex);
    }
  }
  
  showMedia(index) {
    if (index < 0 || index >= this.mediaGallery.mediaItems.length) return;
    
    this.currentIndex = index;
    const item = this.mediaGallery.mediaItems[index];
    
    // Clear previous content
    if (this.mediaWrapper) {
      this.mediaWrapper.innerHTML = '';
      
      // Render media based on type
      if (item.type === 'youtube') {
        const videoId = this.mediaGallery.extractYouTubeId(item.src);
        if (videoId) {
          this.mediaWrapper.innerHTML = `
            <iframe src="https://www.youtube.com/embed/${videoId}?rel=0&autoplay=1" 
                    frameborder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowfullscreen>
            </iframe>
          `;
        }
      } else if (item.type === 'vimeo') {
        const videoId = item.src.split('/').pop().split('?')[0];
        this.mediaWrapper.innerHTML = `
          <iframe src="https://player.vimeo.com/video/${videoId}?autoplay=1" 
                  frameborder="0" 
                  allow="autoplay; fullscreen; picture-in-picture" 
                  allowfullscreen>
          </iframe>
        `;
      } else if (item.type === 'iframe') {
        this.mediaWrapper.innerHTML = `
          <iframe src="${item.src}" 
                  frameborder="0" 
                  allow="autoplay; fullscreen; picture-in-picture" 
                  allowfullscreen>
          </iframe>
        `;
      } else {
        // Image
        this.mediaWrapper.innerHTML = `<img src="${item.src}" alt="${item.alt}">`;
      }
    }
    
    this.updateCounter();
    this.updateNavButtons();
  }
  
  updateCounter() {
    if (this.currentSpan) {
      this.currentSpan.textContent = this.currentIndex + 1;
    }
  }
  
  updateNavButtons() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= this.mediaGallery.mediaItems.length - 1;
    }
  }
}

// Initialize Fullscreen Modal when Media Gallery is ready
document.addEventListener('DOMContentLoaded', function() {
  // Wait for media gallery to be initialized
  setTimeout(() => {
    if (window.mediaGallery && document.querySelector('.media-fullscreen-modal')) {
      window.fullscreenModal = new FullscreenMediaModal(window.mediaGallery);
    }
  }, 100);
});
